import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import query from "../server/constants.api";
import logger from "../server/server";
import useFetch from "../server/useFetch";
import { useAuthContext } from "./AuthContext";

const GlobalFilterContext = createContext(null);

export const useGlobalFilterContext = () => {
  const context = useContext(GlobalFilterContext);

  if (!context) {
    throw new Error(
      "useGlobalFilterContext must be used within an ContentProvider"
    );
  }
  return context;
};

export const GlobalFilterProvider = (props) => {
  const { user, tenant } = useAuthContext();
  const [isToApplyFilters, setIsToApplyFilters] = useState(false); //Triggers certain components to apply filters
  const [dropdownEntity, setDropdownEntity] = useState(null); //Dropdown options
  const [entity, setEntity] = useState(null); //Dropdown value
  const [dropdownSubEntity, setDropdownSubEntity] = useState(null); //Dropdown options
  const [subEntity, setSubEntity] = useState(null); //Dropdown value
  const [mapViewType, setMapViewType] = useState("map"); //Dropdown value
  const [mapPOISelected, setMapPOISelected] = useState(null); //Selected POI on the map

  const [dropdownClasses, setDropdownClasses] = useState([]);
  const [classes, setClasses] = useState(null);
  const [isClassesFetching, setIsClassesFetching] = useState(false);
  const [items, setItems] = useState(null);//Contains the data to be displayed on the map (POIs)
  const [layersDropdown] = useState([
    { value: "Incidents", label: "Incidents_Layer" },
    { value: "Services", label: "Services_Layer" }
  ]);
  const [selectedLayer, setSelectedLayer] = useState("Incidents");
  const [prioritiesDropdown, setPrioritiesDropdown] = useState([]);
  const [prioritiesSelected, setPrioritiesSelected] = useState(null);

  const { data: dataRegions, refetch: refetchRegions } = useFetch("/map/regions", "regionDropdown",
    {
      tenant: "NOVA - Campus de Campolide",
    }, null, null, query.middlewareURL);

  const { data: mapItemsIncidentsData, isFetching: isFetchingMapIncidentsData, refetch: refetchMapItemsIncidents } = useFetch('/incidents/map/items', 'getMapItemsIncidents', {
    // id: "c940f129-7136-495b-9402-348419b1d013",
    tenant: subEntity ? subEntity?.tenant : entity?.tenant,
  }, false, null, query.middlewareCommandURL);

  const { data: mapItemsServicesData, isFetching: isFetchingMapServicesData, refetch: refetchMapItemsServices } = useFetch('/map/items', 'getMapItemsServices', {
    id: "c940f129-7136-495b-9402-348419b1d013",
    layer: "Service",
    tenant: subEntity ? subEntity?.tenant : entity?.tenant,
  }, false, null, query.middlewareURL);

  useEffect(() => {
    if (tenant) {
      refetchRegions();
    }
  }, [tenant])

  useEffect(() => {
    if (subEntity || entity) {
      refetchMapItemsIncidents();
      refetchMapItemsServices();
    }
  }, [entity, subEntity]);

  useEffect(() => {
    if (isToApplyFilters && entity) {
      refetchMapItemsIncidents();
      refetchMapItemsServices();
      setIsToApplyFilters(false);
    }
  }, [isToApplyFilters]);

  useEffect(() => {
    if (dataRegions) {
      const filterObj = {};

      const dropdownCountriesObject = selectSerialize(dataRegions);
      setDropdownEntity(dropdownCountriesObject);
      setEntity(dropdownCountriesObject[0]);
      filterObj.entity = dropdownCountriesObject[0];

      if (dropdownCountriesObject[0]?.cities) {
        const dropdownSubEntityObject = selectSerialize(
          dropdownCountriesObject[0]?.cities
        );
        setDropdownSubEntity(dropdownSubEntityObject);
        setSubEntity(dropdownSubEntityObject[0]);
        filterObj.subEntity = dropdownSubEntityObject[0];
      }

      setIsToApplyFilters(true);
      setIsClassesFetching(true);

      if (Object.keys(dataRegions).length === 0) {
        logger.warn("User: " + user + " Tenant: " + tenant + ", dataRegions is a empty object!");
      }
    }
  }, [dataRegions]);

  useEffect(() => {
    if (selectedLayer === 'Incidents' && mapItemsIncidentsData) {
      populateDropdowns(mapItemsIncidentsData);
    } else if (selectedLayer === 'Services' && mapItemsServicesData) {
      populateDropdowns(mapItemsServicesData);
    }
  }, [mapItemsIncidentsData, mapItemsServicesData, selectedLayer]);

  useEffect(() => {
    if (classes) {
      setIsClassesFetching(false);
    }
  }, [classes]);

  const populateDropdowns = (mapPois) => {
    const pois = mapPois?.filter(x => !x.family?.includes('Facilities') && x.class && x.latitude && x.latitude !== "0" && x.longitude && x.longitude !== "0");
    setItems(pois);

    const poiClasses = pois?.map((x) => { return { label: x.class, value: x.class } }).filter(Boolean);
    const uniqueArray = poiClasses?.filter((item, index, self) =>
      index === self?.findIndex((t) => t?.value === item?.value)
    ) ?? [];//Limpar duplicados

    setClasses(uniqueArray);
    setDropdownClasses(uniqueArray);

    if (selectedLayer === 'Incidents') {
      const incidentsLevels = pois?.map((x) => {
        const newLabel = x.priority.split(' - ')[1];
        return { label: newLabel, value: x.priority };
      }).filter(Boolean);

      const uniqueIncidentsLevels = incidentsLevels?.filter((item, index, self) =>
        index === self.findIndex((t) => t.value === item.value)
      ) ?? [];//Limpar duplicados

      setPrioritiesSelected(uniqueIncidentsLevels);
      setPrioritiesDropdown(uniqueIncidentsLevels);
    }
  };

  const selectEntity = (value) => {
    if (value?.value) {
      const cities = dataRegions?.find((x) => {
        const name = x.name.split(".");
        if (name[0] === value.value) {
          return x;
        }
      })?.cities;

      if (cities) {
        const dropdownSubEntityObject = selectSerialize(cities);
        setDropdownSubEntity(dropdownSubEntityObject);
        setSubEntity(dropdownSubEntityObject[0]);
      } else {
        setDropdownSubEntity(null);
        setSubEntity(null);
      }
      setEntity(value);
    }
  };

  const value = useMemo(() => {
    return {
      // dataRegions,
      isToApplyFilters,
      setIsToApplyFilters,

      dropdownEntity,
      entity,
      selectEntity,

      dropdownSubEntity,
      subEntity,
      setSubEntity,

      mapViewType,
      setMapViewType,

      classes,
      setClasses,
      dropdownClasses,
      setDropdownClasses,
      isClassesFetching,
      setIsClassesFetching,
      items,
      prioritiesSelected,
      setPrioritiesSelected,
      prioritiesDropdown,
      layersDropdown,
      selectedLayer,
      setSelectedLayer,

      mapItemsIncidentsData,
      refetchMapItemsIncidents,
      mapItemsServicesData,
      isFetchingMapServicesData,

      mapPOISelected,
      setMapPOISelected
    };
  }, [selectEntity, entity, subEntity, isToApplyFilters, mapViewType, classes, dropdownClasses, isClassesFetching,
    mapItemsIncidentsData, mapItemsServicesData, isFetchingMapServicesData,
    items, prioritiesSelected, prioritiesDropdown, layersDropdown, selectedLayer, mapPOISelected]);

  return (
    <GlobalFilterContext.Provider value={value}>
      {props.children}
    </GlobalFilterContext.Provider>
  );
};

const selectSerialize = (array) => {
  return array?.sort((a, b) => b.buIndex - a.buIndex)?.map((x) => {
    const name = x.name.split(".");
    return {
      value: name[0],
      label: name[0],
      id: x.id,
      tenant: x.tenant,
      cities: x.cities,
      latitude: x.latitude,
      longitude: x.longitude,
      zoom: x.zoom,
    };
  });
};
